import { Move } from './sort-utils';
import { SectionRowViewModel } from '../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import {
  SectionSortAdditionalDetails,
  SectionSortOption,
  SectionSortProductInfo,
  SectionSortSecondaryCannabinoids,
  SectionSortTerpenes
} from '../models/enum/dto/section-sort-option.enum';
import type { SectionWithProducts } from '../models/menu/section/section-with-products';
import { SortVariantUtils } from './sort-variant-utils';
import { CollapsedSativaHybridIndicaSectionViewModel } from '../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/sativa-hybrid-indica-split-product-section/collapsed-sativa-hybrid-indica-section-view-model';
import { exists } from '../functions/exists';

// @dynamic
export class SortSectionRowViewModelUtils extends SortVariantUtils {
  /* *******************************************************************************************
   *                        Section Row View Model - Sort Options                              *
   * *******************************************************************************************/

  /**
   * Client Dashboard calculates the brand based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the brand from the variants in the row.
   */
  static sectionRowViewModelBrandAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.rowBrand(), b?.rowBrand());
  }
  /**
   * Client Dashboard calculates the brand based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the brand from the variants in the row.
   */
  static sectionRowViewModelBrandDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.rowBrand(), b?.rowBrand());
  }

  /**
   * Client Dashboard calculates the product classification based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the product classification from the variants in the row.
   */
  static sectionRowViewModelVariantClassificationAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.strainTypeSortAsc(a?.rowStrainType(), b?.rowStrainType());
  }
  /**
   * Client Dashboard calculates the product classification based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the product classification from the variants in the row.
   */
  static sectionRowViewModelVariantClassificationDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.strainTypeSortDesc(a?.rowStrainType(), b?.rowStrainType());
  }

  static sectionRowViewModelVariantLineageAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getLineage(), b?.getLineage());
  }
  static sectionRowViewModelVariantLineageDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getLineage(), b?.getLineage());
  }

  /**
   * Client Dashboard calculates the manufacturer based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the manufacturer from the variants in the row.
   */
  static sectionRowViewModelManufacturerAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.rowManufacturer(), b?.rowManufacturer());
  }
  /**
   * Client Dashboard calculates the manufacturer based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the manufacturer from the variants in the row.
   */
  static sectionRowViewModelManufacturerDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.rowManufacturer(), b?.rowManufacturer());
  }

  static sectionRowViewModelPackagedQuantityAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberAscending(a?.getMinPackageQuantity(), b?.getMinPackageQuantity());
  };
  static sectionRowViewModelPackagedQuantityDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberDescending(a?.getMaxPackageQuantity(), b?.getMaxPackageQuantity());
  };

  static sectionRowViewModelPriceAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberAscending(a?.getMinRowPrice(), b?.getMinRowPrice());
  }
  static sectionRowViewModelPriceDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberDescending(a?.getMaxRowPrice(), b?.getMaxRowPrice());
  }

  /**
   * Client Dashboard calculates the product type based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the product type from the variants in the row.
   */
  static sectionRowViewModelProductTypeAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.rowProductType(), b?.rowProductType());
  }
  /**
   * Client Dashboard calculates the product type based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the product type from the variants in the row.
   */
  static sectionRowViewModelProductTypeDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.rowProductType(), b?.rowProductType());
  }

  static sectionRowViewModelSecondaryPriceAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberAscending(a?.getMinRowSecondaryPrice(), b?.getMinRowSecondaryPrice());
  }
  static sectionRowViewModelSecondaryPriceDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberDescending(a?.getMaxRowSecondaryPrice(), b?.getMaxRowSecondaryPrice());
  }

  static sectionRowViewModelStockAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberAscending(a.getMaxRowStock(), b.getMaxRowStock());
  }
  static sectionRowViewModelStockDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberDescending(a?.getMaxRowStock(), b?.getMaxRowStock());
  }

  static sectionRowViewModelTitleAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getRowTitle(), b?.getRowTitle());
  }
  static sectionRowViewModelTitleDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getRowTitle(), b?.getRowTitle());
  }

  static sectionRowViewModelCannabinoidAsc(
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    cannabinoidOrTerpeneCamelCased: string
  ): Move {
    return SortSectionRowViewModelUtils.numberAscendingNegativesAndNullsLast(
      a?.getMinRowCannabinoid(cannabinoidOrTerpeneCamelCased),
      b?.getMinRowCannabinoid(cannabinoidOrTerpeneCamelCased)
    );
  }
  static sectionRowViewModelCannabinoidDesc(
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    cannabinoidOrTerpeneCamelCased: string
  ): Move {
    return SortSectionRowViewModelUtils.numberDescendingNegativesAndNullsLast(
      a?.getMaxRowCannabinoid(cannabinoidOrTerpeneCamelCased),
      b?.getMaxRowCannabinoid(cannabinoidOrTerpeneCamelCased)
    );
  }

  static sectionRowViewModelTerpeneAsc(
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    cannabinoidOrTerpeneCamelCased: string
  ): Move {
    return SortSectionRowViewModelUtils.numberAscendingNegativesAndNullsLast(
      a?.getMinRowTerpene(cannabinoidOrTerpeneCamelCased),
      b?.getMinRowTerpene(cannabinoidOrTerpeneCamelCased)
    );
  }
  static sectionRowViewModelTerpeneDesc(
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    cannabinoidOrTerpeneCamelCased: string
  ): Move {
    return SortSectionRowViewModelUtils.numberDescendingNegativesAndNullsLast(
      a?.getMaxRowTerpene(cannabinoidOrTerpeneCamelCased),
      b?.getMaxRowTerpene(cannabinoidOrTerpeneCamelCased)
    );
  }

  static sectionRowViewModelTACAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberAscendingNegativesAndZerosAndNullsLast(
      a?.getMinRowTotalActiveCannabinoids(),
      b?.getMinRowTotalActiveCannabinoids()
    );
  }
  static sectionRowViewModelTACDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberDescendingNegativesAndZerosAndNullsLast(
      a?.getMaxRowTotalActiveCannabinoids(),
      b?.getMaxRowTotalActiveCannabinoids()
    );
  }

  static sectionRowViewModelTATAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberAscendingNegativesAndZerosAndNullsLast(
      a?.getMinRowTotalActiveTerpenes(),
      b?.getMinRowTotalActiveTerpenes()
    );
  }
  static sectionRowViewModelTATDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.numberDescendingNegativesAndZerosAndNullsLast(
      a?.getMaxRowTotalActiveTerpenes(),
      b?.getMaxRowTotalActiveTerpenes()
    );
  }

  static sectionRowViewModelTopCannabinoidAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getTopCannabinoid(), b?.getTopCannabinoid());
  }
  static sectionRowViewModelTopCannabinoidDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getTopCannabinoid(), b?.getTopCannabinoid());
  }

  static sectionRowViewModelTopTerpeneAsc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getTopTerpene(), b?.getTopTerpene());
  }
  static sectionRowViewModelTopTerpeneDesc(a: SectionRowViewModel, b: SectionRowViewModel): Move {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getTopTerpene(), b?.getTopTerpene());
  }

  static sectionRowViewModelUnitSizeAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberAscending(a?.getMinNumericUnitSize(), b?.getMinNumericUnitSize());
  };
  static sectionRowViewModelUnitSizeDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberDescending(a?.getMaxNumericUnitSize(), b?.getMaxNumericUnitSize());
  };

  /**
   * Client Dashboard calculates the variant type based on the variants that are present for a line item.
   * Therefore, copy the client dashboard and get the variant type from the variants in the row.
   */
  static sectionRowViewModelVariantTypeAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.rowVariantType(), b?.rowVariantType());
  };
  static sectionRowViewModelVariantTypeDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.rowVariantType(), b?.rowVariantType());
  };

  static sectionRowViewModelEffectsAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getEffects(), b?.getEffects());
  };
  static sectionRowViewModelEffectsDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getEffects(), b?.getEffects());
  };

  static sectionRowViewModelAromasAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getAromas(), b?.getAromas());
  };
  static sectionRowViewModelAromasDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getAromas(), b?.getAromas());
  };

  static sectionRowViewModelFlavorGroupAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getFlavorGroup(), b?.getFlavorGroup());
  };
  static sectionRowViewModelFlavorGroupDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getFlavorGroup(), b?.getFlavorGroup());
  };

  static sectionRowViewModelFlavorsAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getFlavors(), b?.getFlavors());
  };
  static sectionRowViewModelFlavorsDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getFlavors(), b?.getFlavors());
  };

  static sectionRowViewModelHelpsWithAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getHelpsWith(), b?.getHelpsWith());
  };
  static sectionRowViewModelHelpsWithDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getHelpsWith(), b?.getHelpsWith());
  };

  static sectionRowViewModelAppearancesAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getAppearances(), b?.getAppearances());
  };
  static sectionRowViewModelAppearancesDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getAppearances(), b?.getAppearances());
  };

  static sectionRowViewModelGrowingConditionsAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getGrowingConditions(), b?.getGrowingConditions());
  };
  static sectionRowViewModelGrowingConditionsDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getGrowingConditions(), b?.getGrowingConditions());
  };

  static sectionRowViewModelEnergyScoreAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberAscendingNegativesAndNullsLast(
      a?.getMinEnergyScore(),
      b?.getMinEnergyScore()
    );
  };
  static sectionRowViewModelEnergyScoreDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.numberDescendingNegativesAndNullsLast(
      a?.getMaxEnergyScore(),
      b?.getMaxEnergyScore()
    );
  };

  static sectionRowViewModelCraftDesignationsAsc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringAscendingNullsLast(a?.getCraftDesignations(), b?.getCraftDesignations());
  };
  static sectionRowViewModelCraftDesignationsDesc = (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
    return SortSectionRowViewModelUtils.stringDescendingNullsLast(a?.getCraftDesignations(), b?.getCraftDesignations());
  };

  static sectionRowViewModelTagsAsc = (
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    section: SectionWithProducts
  ): Move => {
    return SortSectionRowViewModelUtils.variantListTagsAsc(
      a?.getScopedVisibleVariantsElseRowVariants(),
      b?.getScopedVisibleVariantsElseRowVariants(),
      section
    );
  };
  static sectionRowViewModelTagsDesc = (
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    section: SectionWithProducts
  ): Move => {
    return SortSectionRowViewModelUtils.variantListTagsDesc(
      a?.getScopedVisibleVariantsElseRowVariants(),
      b?.getScopedVisibleVariantsElseRowVariants(),
      section
    );
  };

  static sectionRowViewModelTagGroupsAsc = (
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    section: SectionWithProducts
  ): Move => {
    return SortSectionRowViewModelUtils.variantListTagGroupsAsc(
      a?.getScopedVisibleVariantsElseRowVariants(),
      b?.getScopedVisibleVariantsElseRowVariants(),
      section
    );
  };
  static sectionRowViewModelTagGroupsDesc = (
    a: SectionRowViewModel,
    b: SectionRowViewModel,
    section: SectionWithProducts
  ): Move => {
    return SortSectionRowViewModelUtils.variantListTagGroupsDesc(
      a?.getScopedVisibleVariantsElseRowVariants(),
      b?.getScopedVisibleVariantsElseRowVariants(),
      section
    );
  };

  static getSectionRowSortLambda(
    sortType: SectionSortOption,
    section: SectionWithProducts
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    switch (true) {
      case Object.values(SectionSortProductInfo).includes(sortType as SectionSortProductInfo):
        return SortSectionRowViewModelUtils.getSectionRowSortLambdaProductInfo(sortType);
      case Object.values(SectionSortAdditionalDetails).includes(sortType as SectionSortAdditionalDetails):
        return SortSectionRowViewModelUtils.getSectionRowSortLambdaAdditionalDetails(sortType, section);
      case Object.values(SectionSortSecondaryCannabinoids).includes(sortType as SectionSortSecondaryCannabinoids):
        return SortSectionRowViewModelUtils.getSectionRowSortLambdaSecondaryCannabinoid(sortType);
      case Object.values(SectionSortTerpenes).includes(sortType as SectionSortTerpenes):
        return SortSectionRowViewModelUtils.getSectionRowSortLambdaTerpene(sortType);
    }
  }

  private static getSectionRowSortLambdaProductInfo(
    sortType: SectionSortOption
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    switch (sortType) {
      case SectionSortProductInfo.BrandAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelBrandAsc;
      case SectionSortProductInfo.BrandDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelBrandDesc;
      case SectionSortProductInfo.CBDAsc:
        return (a: SectionRowViewModel, b: SectionRowViewModel) => {
          return SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidAsc(a, b, 'CBD');
        };
      case SectionSortProductInfo.CBDDesc:
        return (a: SectionRowViewModel, b: SectionRowViewModel) => {
          return SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidDesc(a, b, 'CBD');
        };
      case SectionSortProductInfo.ClassificationAsc: {
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantClassificationAsc;
      }
      case SectionSortProductInfo.ClassificationDesc: {
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantClassificationDesc;
      }
      case SectionSortProductInfo.LineageAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantLineageAsc;
      case SectionSortProductInfo.LineageDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantLineageDesc;
      case SectionSortProductInfo.ManufacturerAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelManufacturerAsc;
      case SectionSortProductInfo.ManufacturerDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelManufacturerDesc;
      case SectionSortProductInfo.PackagedQuantityAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelPackagedQuantityAsc;
      case SectionSortProductInfo.PackagedQuantityDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelPackagedQuantityDesc;
      case SectionSortProductInfo.PriceAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelPriceAsc;
      case SectionSortProductInfo.PriceDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelPriceDesc;
      case SectionSortProductInfo.ProductTypeAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelProductTypeAsc;
      case SectionSortProductInfo.ProductTypeDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelProductTypeDesc;
      case SectionSortProductInfo.SecondaryPriceAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelSecondaryPriceAsc;
      case SectionSortProductInfo.SecondaryPriceDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelSecondaryPriceDesc;
      case SectionSortProductInfo.StockAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelStockAsc;
      case SectionSortProductInfo.StockDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelStockDesc;
      case SectionSortProductInfo.TACAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTACAsc;
      case SectionSortProductInfo.TACDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTACDesc;
      case SectionSortProductInfo.TitleAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTitleAsc;
      case SectionSortProductInfo.TitleDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTitleDesc;
      case SectionSortProductInfo.THCAsc:
        return (a: SectionRowViewModel, b: SectionRowViewModel) => {
          return SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidAsc(a, b, 'THC');
        };
      case SectionSortProductInfo.THCDesc:
        return (a: SectionRowViewModel, b: SectionRowViewModel) => {
          return SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidDesc(a, b, 'THC');
        };
      case SectionSortProductInfo.TopCannabinoidAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTopCannabinoidAsc;
      case SectionSortProductInfo.TopCannabinoidDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTopCannabinoidDesc;
      case SectionSortProductInfo.TopTerpeneAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTopTerpeneAsc;
      case SectionSortProductInfo.TopTerpeneDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTopTerpeneDesc;
      case SectionSortProductInfo.TotalTerpeneAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTATAsc;
      case SectionSortProductInfo.TotalTerpeneDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelTATDesc;
      case SectionSortProductInfo.UnitSizeAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelUnitSizeAsc;
      case SectionSortProductInfo.UnitSizeDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelUnitSizeDesc;
      case SectionSortProductInfo.VariantTypeAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantTypeAsc;
      case SectionSortProductInfo.VariantTypeDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelVariantTypeDesc;
      default:
        return SortSectionRowViewModelUtils.sectionRowViewModelTitleAsc;
    }
  }

  private static getSectionRowSortLambdaAdditionalDetails(
    sortType: SectionSortOption,
    section: SectionWithProducts
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    switch (sortType) {
      case SectionSortAdditionalDetails.EffectsAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelEffectsAsc;
      case SectionSortAdditionalDetails.EffectsDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelEffectsDesc;
      case SectionSortAdditionalDetails.AromasAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelAromasAsc;
      case SectionSortAdditionalDetails.AromasDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelAromasDesc;
      case SectionSortAdditionalDetails.FlavorGroupAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelFlavorGroupAsc;
      case SectionSortAdditionalDetails.FlavorGroupDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelFlavorGroupDesc;
      case SectionSortAdditionalDetails.FlavorsAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelFlavorsAsc;
      case SectionSortAdditionalDetails.FlavorsDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelFlavorsDesc;
      case SectionSortAdditionalDetails.HelpsWithAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelHelpsWithAsc;
      case SectionSortAdditionalDetails.HelpsWithDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelHelpsWithDesc;
      case SectionSortAdditionalDetails.AppearancesAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelAppearancesAsc;
      case SectionSortAdditionalDetails.AppearancesDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelAppearancesDesc;
      case SectionSortAdditionalDetails.GrowingConditionsAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelGrowingConditionsAsc;
      case SectionSortAdditionalDetails.GrowingConditionsDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelGrowingConditionsDesc;
      case SectionSortAdditionalDetails.EnergyScoreAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelEnergyScoreAsc;
      case SectionSortAdditionalDetails.EnergyScoreDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelEnergyScoreDesc;
      case SectionSortAdditionalDetails.CraftDesignationsAsc:
        return SortSectionRowViewModelUtils.sectionRowViewModelCraftDesignationsAsc;
      case SectionSortAdditionalDetails.CraftDesignationsDesc:
        return SortSectionRowViewModelUtils.sectionRowViewModelCraftDesignationsDesc;
      case SectionSortAdditionalDetails.TagsAsc:
        return (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
          return SortSectionRowViewModelUtils.sectionRowViewModelTagsAsc(a, b, section);
        };
      case SectionSortAdditionalDetails.TagsDesc:
        return (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
          return SortSectionRowViewModelUtils.sectionRowViewModelTagsDesc(a, b, section);
        };
      case SectionSortAdditionalDetails.TagGroupsAsc:
        return (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
          return SortSectionRowViewModelUtils.sectionRowViewModelTagGroupsAsc(a, b, section);
        };
      case SectionSortAdditionalDetails.TagGroupsDesc:
        return (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
          return SortSectionRowViewModelUtils.sectionRowViewModelTagGroupsDesc(a, b, section);
        };
      default:
        return SortSectionRowViewModelUtils.sectionRowViewModelTitleAsc;
    }
  }

  private static getSectionRowSortLambdaSecondaryCannabinoid(
    sortType: SectionSortOption
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    const { cannabinoid, order } = SortSectionRowViewModelUtils.getCannabinoidAccessor(sortType);
    const isAscending = order === 'ASC';
    const asc = SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidAsc;
    const desc = SortSectionRowViewModelUtils.sectionRowViewModelCannabinoidDesc;
    return isAscending
      ? (a: SectionRowViewModel, b: SectionRowViewModel): Move => asc(a, b, cannabinoid)
      : (a: SectionRowViewModel, b: SectionRowViewModel): Move => desc(a, b, cannabinoid);
  }

  private static getSectionRowSortLambdaTerpene(
    sortType: SectionSortOption
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    const { terpeneCamelCased, order } = SortSectionRowViewModelUtils.getTerpeneAccessor(sortType);
    const isAscending = order === 'ASC';
    const asc = SortSectionRowViewModelUtils.sectionRowViewModelTerpeneAsc;
    const desc = SortSectionRowViewModelUtils.sectionRowViewModelTerpeneDesc;
    return isAscending
      ? (a: SectionRowViewModel, b: SectionRowViewModel) => asc(a, b, terpeneCamelCased)
      : (a: SectionRowViewModel, b: SectionRowViewModel) => desc(a, b, terpeneCamelCased);
  }

  static sortSectionRowViewModelsInSection(
    section: SectionWithProducts
  ): (a: SectionRowViewModel, b: SectionRowViewModel) => Move {
    const primary = section?.sorting;
    const secondary = section?.secondarySorting;
    const tertiary = SortSectionRowViewModelUtils.DEFAULT_SECTION_TERTIARY_SORT;
    const secondarySortId = SortSectionRowViewModelUtils.sharedSortId(secondary);
    const tertiarySortId = SortSectionRowViewModelUtils.sharedSortId(tertiary);
    const sortByTertiary = secondarySortId !== tertiarySortId;
    const sorter = SortSectionRowViewModelUtils.getSectionRowSortLambda;
    return (a: SectionRowViewModel, b: SectionRowViewModel): Move => {
      // short-circuit calculation
      return (
        sorter(primary, section)(a, b) ||
        (exists(secondary) ? sorter(secondary, section)(a, b) : Move.Nothing) ||
        (sortByTertiary ? sorter(tertiary, section)(a, b) : Move.Nothing)
      );
    };
  }

  static plantlifeSectionRowSortCompare(s: SectionWithProducts, a: SectionRowViewModel, b: SectionRowViewModel): Move {
    const invisibleToMiddle = (c: SectionRowViewModel, d: SectionRowViewModel) => {
      const cIsInvisible = c instanceof CollapsedSativaHybridIndicaSectionViewModel;
      const dIsInvisible = d instanceof CollapsedSativaHybridIndicaSectionViewModel;
      if (cIsInvisible && !dIsInvisible) return Move.ARight;
      else return Move.Nothing;
    };
    const pushVariantsMovedToCenterColumnToEnd = (c: SectionRowViewModel, d: SectionRowViewModel) => {
      const cMovedToCenter = c?.rowVariants?.some(v => v.moveToCenterColumn);
      const dMovedToCenter = d?.rowVariants?.some(v => v.moveToCenterColumn);
      if (cMovedToCenter && !dMovedToCenter) return Move.ARight;
      else if (dMovedToCenter && !cMovedToCenter) return Move.BRight;
      else return Move.Nothing;
    };
    const regularSorting = SortSectionRowViewModelUtils.sortSectionRowViewModelsInSection(s);
    return pushVariantsMovedToCenterColumnToEnd(a, b) || invisibleToMiddle(a, b) || regularSorting(a, b);
  }
}
